import { getBooking } from "../../../app/api/getBooking";
import { getStops } from "../../../app/api/getStops";
import { BaseAction } from "../../../core/actions/BaseAction";
import { calculatePrice, convertISO8601ToDuration, convertISO8601ToMinutes, determinePassengerType, getUpperPassengerLabelByAge } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IBookedOffer, IBooking, IBookingPassenger, IBookingTypeTrip, IDeterminePassenger, ISelectedPassenger, IStop, PassengerPriceDetails, SearchStateType } from "../../../Orchestrator/Types/Index";
import { BookingPassenger, BookingTypeTrip, IBookingResponse, Product3 } from "../../../Orchestrator/Types/Types/BookingTypes";
import { PriceCategory, PriceInfo, TrainType } from "../../../Orchestrator/Types/Types/TrainTypes";
import { IGetBookingDetailsActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/BookingDetailActionParams";
import { TrainTypeEnum, TripTypeEnum, isShuttleOffer } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import stopManager from "../../../Lib/Utils/StopManager";

interface IGetBookingActionResult {
    data: IBookingResponse | null;
    allFulFillOutBoundPassIds: string[];
    outBoundPassengerPriceList: PassengerPriceDetails[];
    allBookedOfferWithoutExchange: IBookedOffer[];
    allBookedTripsWithoutExchange: IBookingTypeTrip[];
    message: string;
    status: string;
}

class GetBookingAction extends BaseAction<IGetBookingActionResult, IGetBookingDetailsActionParams> {
    override async run(): Promise<IGetBookingActionResult | null> {
        let actionResult: IGetBookingActionResult = {
            data: null,
            allFulFillOutBoundPassIds: [],
            outBoundPassengerPriceList: [],
            allBookedOfferWithoutExchange: [],
            allBookedTripsWithoutExchange: [],
            message: "",
            status: "fail"
        }
        try {
            const { bookingId, direction, displayPassengerData, selectedTripsData } = this.params!;
            const response: any = await getBooking(bookingId);
            if (response.status === 'ok' && response.answer) {
                let bookingResponse: IBookingResponse = response.answer;

                // Apply filtering logic similar to GetBookingDetailDataAction
                // selectedTripsData path FIRST — CDT split cards have both direction AND tripIds/passengerData,
                // so the more specific selectedTripsData filtering must take priority over direction-only filtering.
                if (selectedTripsData?.tripIds && selectedTripsData?.tripIds?.length > 0 && selectedTripsData?.passengerData) {
                    const allTrips = bookingResponse.booking?.trips || [];
                    const allBookedOffers = bookingResponse.booking?.bookedOffers || [];
                    const allPassengers = bookingResponse.booking?.passengers || [];

                    const keptTrips = allTrips.filter(trip => selectedTripsData.tripIds.includes(trip.id));
                    const keptTripIds = keptTrips.map(trip => trip.id);

                    // Collect passenger IDs from passengerData for admission scoping
                    const outboundPassengerIds = new Set<string>(selectedTripsData.passengerData?.outbound || []);
                    const inboundPassengerIds = new Set<string>(selectedTripsData.passengerData?.inbound || []);
                    const combinedPassengerIds = new Set<string>([...outboundPassengerIds, ...inboundPassengerIds]);
                    const totalBookingPassengers = allPassengers.length;
                    const isPartialPassengerView = combinedPassengerIds.size > 0 && combinedPassengerIds.size < totalBookingPassengers;

                    const filteredOffers = allBookedOffers
                        .filter((offer: any) => keptTripIds.includes(offer.tripCoverage?.coveredTripId))
                        .map((offer: any) => {
                            if (combinedPassengerIds.size === 0) return offer;
                            const scopedAdmissions = (offer.admissions || []).filter((admission: any) => {
                                const passengerMatch = admission.passengerIds?.some((pid: string) => combinedPassengerIds.has(pid));
                                const statusMatch = !isPartialPassengerView || admission.status === "FULFILLED";
                                return passengerMatch && statusMatch;
                            });
                            return { ...offer, admissions: scopedAdmissions };
                        })
                        .filter((offer: any) => (offer.admissions || []).length > 0);

                    // Filter passengers based on displayPassengerData or passengerData
                    let filteredPassengers = allPassengers;
                    if (displayPassengerData && displayPassengerData.length > 0) {
                        const passengerIds = displayPassengerData.map((data: any) => data.id);
                        filteredPassengers = allPassengers.filter((passenger) =>
                            passengerIds.includes(passenger.id)
                        );
                    } else if (combinedPassengerIds.size > 0) {
                        filteredPassengers = allPassengers.filter((passenger) =>
                            combinedPassengerIds.has(passenger.id)
                        );
                    }

                    const updatedBooking = {
                        ...bookingResponse.booking,
                        trips: keptTrips,
                        bookedOffers: filteredOffers,
                        passengers: filteredPassengers
                    };

                    bookingResponse = {
                        ...bookingResponse,
                        booking: updatedBooking
                    };
                } else if (direction) {
                    const allTrips = bookingResponse.booking?.trips || [];
                    const allBookedOffers = bookingResponse.booking?.bookedOffers || [];
                    const allPassengers = bookingResponse.booking?.passengers || [];

                    let directionToKeep = "";
                    let directionToRemove = "";

                    if (direction === TrainTypeEnum.outbound || direction === TripTypeEnum.OUT_BOUND) {
                        directionToKeep = TripTypeEnum.OUT_BOUND;
                        directionToRemove = TripTypeEnum.IN_BOUND;
                    } else if (direction === TrainTypeEnum.inbound || direction === TripTypeEnum.IN_BOUND) {
                        directionToKeep = TripTypeEnum.IN_BOUND;
                        directionToRemove = TripTypeEnum.OUT_BOUND;
                    }

                    if (directionToKeep && directionToRemove) {
                        // Filter trips to keep only the desired direction
                        let keptTrips = allTrips.filter(trip => trip.direction === directionToKeep);

                        // If multiple keptTrips, check for single-leg trips with inactive legs and remove them
                        if (keptTrips.length > 1) {
                            keptTrips = keptTrips.filter((trip: any) => {
                                if (Array.isArray(trip.legs) && trip.legs.length === 1) {
                                    return trip.legs[0]?.isActive === true;
                                }
                                return true; // keep trips with multiple legs or no legs
                            });
                        }

                        // Identify IDs of removed trips (opposite direction)
                        const removedTripIds = allTrips
                            .filter(trip => trip.direction === directionToRemove)
                            .map(trip => trip.id);

                        // Filter bookedOffers to exclude those linked to removed trips
                        const filteredOffers = allBookedOffers.filter((offer: any) => {
                            const coveredId = offer.tripCoverage?.coveredTripId;
                            return !removedTripIds.includes(coveredId);
                        });

                        // If displayPassengerData is provided, filter passengers by the IDs in displayPassengerData
                        let filteredPassengers = allPassengers;
                        if (displayPassengerData && displayPassengerData.length > 0) {
                            const displayPassengerIds = displayPassengerData.map((data: any) => data.id);
                            filteredPassengers = allPassengers.filter((passenger) =>
                                displayPassengerIds.includes(passenger.id)
                            );
                        }

                        // Construct updated booking object
                        const updatedBooking = {
                            ...bookingResponse.booking,
                            trips: keptTrips,
                            bookedOffers: filteredOffers,
                            passengers: filteredPassengers
                        };

                        bookingResponse = {
                            ...bookingResponse,
                            booking: updatedBooking
                        };
                    }
                }
                // Continue with existing logic using the filtered bookingResponse
                const allBookedOfferWithoutExchange: IBookedOffer[] = this.getAllOfferWithoutExchange(bookingResponse.booking.bookedOffers);
                let allTripWithoutExchange: IBookingTypeTrip[] = this.getAllTripWithoutExchange(allBookedOfferWithoutExchange, bookingResponse.booking.trips);

                // const allBookedOfferWithoutExchange: IBookedOffer[] = bookingResponse.booking.bookedOffers;
                // let allTripWithoutExchange: BookingTypeTrip[] = bookingResponse.booking.trips;
                //updateRecoilState("bookingResponseAtom", bookingResponse);

                //Code added for - One way shuttle + Train
                let isShuttleTrainStop = false;
                const processedTrips: BookingTypeTrip[] = [];
                // allTripWithoutExchange.forEach(originalTrip => {
                //     if (originalTrip.legs && originalTrip.legs.length > 1) {
                //         isShuttleTrainStop = true; // Set flag if any trip is split
                //         originalTrip.legs.forEach(leg => {
                //             const newTrip = structuredClone(originalTrip); // Clone to carry over properties like direction
                //             newTrip.legs = [leg];
                //             newTrip.origin = leg.timedLeg?.start?.stopPlaceRef;
                //             newTrip.destination = leg.timedLeg?.end?.stopPlaceRef;
                //             // Set a preliminary trainType based on the leg's mode. This might be refined later by more specific logic.
                //             newTrip.trainType = leg?.timedLeg?.service?.mode?.ptMode === "TRAIN" ? "train" : "shuttle";
                //             newTrip.duration = leg?.timedLeg?.duration || "";
                //             newTrip.startTime = leg?.timedLeg?.start?.serviceDeparture?.timetabledTime || "";
                //             newTrip.endTime = leg?.timedLeg?.end?.serviceArrival?.timetabledTime || "";
                //             processedTrips.push(newTrip);
                //         });
                //     } else {
                //         processedTrips.push(originalTrip); // Add single-leg trips as is
                //     }
                // });
                //allTripWithoutExchange = processedTrips;

                let shuttleTrips;
                let filteredTrips;
                if (isShuttleTrainStop) {
                    shuttleTrips = allTripWithoutExchange.filter(trip => {
                        const shuttleTripIds = this.getShuttleTripIdsForShuttleTrain(allBookedOfferWithoutExchange);
                        return shuttleTripIds.includes(trip?.legs[0]?.id);
                    });

                    filteredTrips = allTripWithoutExchange.filter(trip => {
                        const shuttleTripIds = this.getShuttleTripIdsForShuttleTrain(allBookedOfferWithoutExchange);
                        return !shuttleTripIds.includes(trip?.legs[0]?.id);
                    });
                    allTripWithoutExchange = [...filteredTrips];
                }
                else {
                    shuttleTrips = allTripWithoutExchange.filter(trip => {
                        const shuttleTripIds = this.getShuttleTripIds(allBookedOfferWithoutExchange);
                        return shuttleTripIds.includes(trip.id);
                    });
                    //console.log('shuttle trips:', shuttleTrips);

                    filteredTrips = allTripWithoutExchange.filter(trip => {
                        const shuttleTripIds = this.getShuttleTripIds(allBookedOfferWithoutExchange);
                        return !shuttleTripIds.includes(trip.id);
                    });
                    allTripWithoutExchange = (filteredTrips?.length || 0) === 0 ? shuttleTrips : filteredTrips;
                }

                const tripStatus = allTripWithoutExchange.length > 1 ? "roundTrip" : "oneWayTrip";
                updateRecoilState("selectedBookingTripStatusAtom", tripStatus);

                //Find outbound shuttle trips and inbound shuttle trips
                //Outbound shuttle trips - origin and destination
                let outboundOriginShuttleTrip = null;
                let outboundDestinationShuttleTrip = null;
                if (filteredTrips?.length > 0 && shuttleTrips?.length > 0) {
                    outboundOriginShuttleTrip = shuttleTrips?.find(trip => trip.destination?.stopPlaceRef === filteredTrips[0]?.origin?.stopPlaceRef);
                    outboundDestinationShuttleTrip = isShuttleTrainStop
                        ? shuttleTrips?.find(trip => trip.origin?.stopPlaceRef === filteredTrips[0]?.destination?.stopPlaceRef)
                        : shuttleTrips?.find(trip => trip.destination?.stopPlaceRef === filteredTrips[0]?.destination?.stopPlaceRef);
                }

                //Inbound shuttle trips - origin and destination
                let inboundOriginShuttleTrip = null;
                let inboundDestinationShuttleTrip = null;
                if (filteredTrips?.length > 1 && shuttleTrips?.length > 0) {
                    inboundOriginShuttleTrip = shuttleTrips?.find(trip => trip.origin?.stopPlaceRef === filteredTrips[1]?.origin?.stopPlaceRef);
                    inboundDestinationShuttleTrip = shuttleTrips?.find(trip => trip.origin?.stopPlaceRef === filteredTrips[1]?.destination?.stopPlaceRef);
                }


                const passengerByAgeOfInAndOutBound = await this.calculatePassengerListByPrice(bookingResponse)
                // Capture the outbound passenger price list
                const outBoundPassengerPriceList = passengerByAgeOfInAndOutBound.outBoundPassengerPriceList;
                const passengerList: ISelectedPassenger[] = bookingResponse.booking.passengers.map(passenger => {
                    const passengerObject: ISelectedPassenger = {
                        id: passenger.id,
                        firstName: passenger?.detail?.firstName || "",
                        lastName: passenger?.detail?.lastName || "",
                        email: passenger?.detail?.email || "",
                        mobile: "",
                        remember: false,
                        age: passenger.age,
                        seatingDetails: null,
                        cards: passenger.cards || [],
                        prmNeeds: passenger.prmNeeds || "",
                    }
                    return passengerObject
                });
                updateRecoilState("selectedPassengersAtom", passengerList);


                const tripStatusForSelection: string[] = [];

                // Get only Fulfilled passenger id to cancel, if there.
                const allFulFillOutBoundPassIds = outBoundPassengerPriceList.
                    filter((d: PassengerPriceDetails) => d.pStatus == "FULFILLED").map((d: PassengerPriceDetails) => d.pId)
                if (allFulFillOutBoundPassIds.length > 0) {
                    updateRecoilState("selectedPassengerForCancelAtom", allFulFillOutBoundPassIds)
                    tripStatusForSelection.push(TrainTypeEnum.outbound)
                }

                const tripStatusForDisable: string[] = []

                // Check all the outbound passenger are refunded or not.
                const allOutBoundRefundedPassIds = outBoundPassengerPriceList.
                    filter((d: PassengerPriceDetails) => d.pStatus == "REFUNDED" || d.pStatus == "RELEASED").map((d: PassengerPriceDetails) => d.pId)
                if (allOutBoundRefundedPassIds.length === passengerList.length) {
                    tripStatusForDisable.push(TrainTypeEnum.outbound)
                }
                if (passengerByAgeOfInAndOutBound.inBoundPassengerPriceList?.length > 0) {
                    // Get only Fulfilled passenger id to cancel, if there.
                    const allFulFillInBoundPassIds = passengerByAgeOfInAndOutBound.inBoundPassengerPriceList.filter((d: any) => d.pStatus == "FULFILLED").map((d: any) => d.pId)
                    if (allFulFillInBoundPassIds.length > 0) {
                        tripStatusForSelection.push(TrainTypeEnum.inbound)
                        updateRecoilState("selectedPassengerForCancelInBoundAtom", allFulFillInBoundPassIds)
                    }
                    // Check all the inbound passenger are refunded or not.
                    const allInBoundRefundedPassIds = passengerByAgeOfInAndOutBound.inBoundPassengerPriceList.filter((d: any) => d.pStatus == "REFUNDED" || d.pStatus == "RELEASED").map((d: any) => d.pId)
                    if (allInBoundRefundedPassIds.length === passengerList.length) {
                        tripStatusForDisable.push(TrainTypeEnum.inbound)
                    }
                }
                updateRecoilState("tripDisableForCancelSelectionAtom", tripStatusForDisable)
                updateRecoilState('bookingResponseAtom', bookingResponse);

                const priceCategory: PriceCategory = {
                    price: 0,
                    isSoldOut: false,
                    discount: 0,
                    seatsLeft: 0,
                    offerId: ""
                }
                const priceInfo: PriceInfo = {
                    standardProducts: [],
                    premiumProducts: [],
                    shuttle: priceCategory,
                    standardValue: priceCategory,
                    standardPro: priceCategory,
                    premium: priceCategory
                }
                const stopResponse: any = await getStops();
                let stops: any = []
                if (stopResponse.status === "ok" && stopResponse.answer) {
                    stops = stopResponse.answer.stops;
                }

                const passengersObject: { [key: string]: number } = {};
                passengerList.forEach((passenger) => {
                    const determinePassenger: IDeterminePassenger = {
                        age: passenger?.age,
                        cards: passenger?.cards || [],
                        prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
                    }
                    const passengerType = determinePassengerType(determinePassenger);

                    // Initialize the count for the passenger type if not already present
                    if (!passengersObject[passengerType]) {
                        passengersObject[passengerType] = 0;
                    }

                    // Increment the count for this passenger type
                    passengersObject[passengerType] += 1;
                });
                updateRecoilState("selectedPassengersAtom", passengerList) // All passenger

                const listOfPassengers: BookingPassenger =
                    await this.getPassengerListByOfferAdmissions(passengerList, allTripWithoutExchange, allBookedOfferWithoutExchange)
                if (listOfPassengers.outbound) {
                    updateRecoilState("selectedPassengersOutBoundAtom", listOfPassengers.outbound);
                    const totalOutBound = listOfPassengers.outbound.reduce(
                        (sum, obj) => sum + obj.passengerPrice,
                        0
                    );
                    updateRecoilState("alreadyPaidOutBoundAtom", totalOutBound ? totalOutBound : 0);
                }

                if (listOfPassengers.inbound) {
                    updateRecoilState("selectedPassengersInBoundAtom", listOfPassengers.inbound);
                    const totalInBound = listOfPassengers.inbound.reduce(
                        (sum, obj) => sum + obj.passengerPrice,
                        0
                    );
                    updateRecoilState("alreadyPaidInBoundAtom", totalInBound ? totalInBound : 0);
                }

                updateRecoilState("selectedTrainStateAtom", null);
                //updateRecoilState("searchStateAtom", null);
                updateRecoilState("selectedDateAtom", null);

                updateRecoilState("selectedInboundTrainStateAtom", null);
                updateRecoilState("inboundSearchStateAtom", null);
                updateRecoilState("inboundSelectedDateAtom", "");

                for (let i = 0; i < allTripWithoutExchange.length; i++) {
                    const trip = allTripWithoutExchange[i];
                    const bookedOffer = isShuttleTrainStop ? allBookedOfferWithoutExchange.find(offer => offer.tripCoverage.coveredLegIds[0]
                        === trip.legs[0]?.id) : allBookedOfferWithoutExchange.find(offer => offer.tripCoverage.coveredTripId === trip.id);
                    const originShuttlebookedOffer = allBookedOfferWithoutExchange?.find(
                        offer => offer.tripCoverage.coveredTripId === outboundOriginShuttleTrip?.id
                    );
                    const destinationShuttlebookedOffer = isShuttleTrainStop ?
                        allBookedOfferWithoutExchange?.find(
                            offer => offer.tripCoverage.coveredLegIds[0] === outboundDestinationShuttleTrip?.legs[0]?.id)
                        : allBookedOfferWithoutExchange?.find(
                            offer => offer.tripCoverage.coveredTripId === outboundDestinationShuttleTrip?.id
                        );
                    const originInboundShuttlebookedOffer = allBookedOfferWithoutExchange?.find(
                        offer => offer.tripCoverage.coveredTripId === inboundOriginShuttleTrip?.id
                    );
                    const destinationInboundShuttlebookedOffer = allBookedOfferWithoutExchange?.find(
                        offer => offer.tripCoverage.coveredTripId === inboundDestinationShuttleTrip?.id
                    );
                    //console.log("bookedOffer", bookedOffer)
                    const bookedTrips: TrainType = {
                        id: bookingResponse.booking.id,
                        departureTime: this.convertTimeToArabic(trip.startTime),
                        arrivalTime: this.convertTimeToArabic(trip.endTime),
                        type: bookingResponse.booking.passengers[0]?.type || 'Unknown',
                        duration: convertISO8601ToDuration(trip.duration),
                        durationInMinutes: convertISO8601ToMinutes(trip.duration),
                        trainNumbers: trip?.legs[0]?.timedLeg?.service?.vehicleNumbers?.[0] || '',
                        stops: trip.transfers > 0 ? Array(trip.transfers).fill('Stop') : [],
                        priceInfo: priceInfo,
                        isSoldOut: false,
                        selectedPriceClass: this.getTrainClassTypeBaseOnProduct(bookedOffer?.products || []),
                        trainNo: trip?.legs[0]?.timedLeg?.service?.vehicleNumbers?.[0] || '',
                        transfers: trip.transfers,
                        trainType: isShuttleOffer(bookedOffer) ? "shuttle" : outboundOriginShuttleTrip ? "shuttleTrain" : "train",
                        descriptionText: bookedOffer?.products[0]?.descriptiveTexts[0]?.description,
                        originShuttle: filteredTrips.length === 0 ? undefined : outboundOriginShuttleTrip ? {
                            ...outboundOriginShuttleTrip,
                            id: bookingResponse.booking.id,
                            departureTime: this.convertTimeToArabic(outboundOriginShuttleTrip.startTime),
                            arrivalTime: this.convertTimeToArabic(outboundOriginShuttleTrip.endTime),
                            type: bookingResponse.booking.passengers[0]?.type || "Unknown",
                            duration: convertISO8601ToDuration(outboundOriginShuttleTrip.duration),
                            trainNumbers: outboundOriginShuttleTrip?.legs?.[0]?.timedLeg?.service?.vehicleNumbers?.[0] || "",
                            stops: outboundOriginShuttleTrip.transfers > 0 ? Array(outboundOriginShuttleTrip.transfers).fill("Stop") : [],
                            priceInfo: originShuttlebookedOffer?.admissions?.[0]?.price ? calculatePrice(originShuttlebookedOffer.admissions[0].price) : '',
                            isSoldOut: false,
                            selectedPriceClass: this.getTrainClassTypeBaseOnProduct(bookedOffer?.products || []),
                            trainNo: outboundOriginShuttleTrip?.legs?.[0]?.timedLeg?.service?.vehicleNumbers?.[0] || "",
                            transfers: outboundOriginShuttleTrip.transfers,
                            fromName: outboundOriginShuttleTrip?.legs?.[0]?.timedLeg?.start?.stopPlaceName || "",
                            toName: outboundOriginShuttleTrip?.legs?.[0]?.timedLeg?.end?.stopPlaceName || "",
                            trainType: "shuttle"
                        } : undefined,
                        destinationShuttle: filteredTrips.length === 0 ? undefined : outboundDestinationShuttleTrip ? {
                            ...outboundDestinationShuttleTrip,
                            id: bookingResponse.booking.id,
                            departureTime: this.convertTimeToArabic(outboundDestinationShuttleTrip.startTime),
                            arrivalTime: this.convertTimeToArabic(outboundDestinationShuttleTrip.endTime),
                            type: bookingResponse.booking.passengers[0]?.type || "Unknown",
                            duration: convertISO8601ToDuration(outboundDestinationShuttleTrip.duration),
                            trainNumbers: outboundDestinationShuttleTrip?.legs?.[0]?.timedLeg?.service?.vehicleNumbers?.[0] || "",
                            stops: outboundDestinationShuttleTrip.transfers > 0 ? Array(outboundDestinationShuttleTrip.transfers).fill("Stop") : [],
                            priceInfo: destinationShuttlebookedOffer?.admissions?.[0]?.price
                                ? calculatePrice(destinationShuttlebookedOffer.admissions[0].price) : '',
                            isSoldOut: false,
                            selectedPriceClass: this.getTrainClassTypeBaseOnProduct(bookedOffer?.products || []),
                            trainNo: outboundDestinationShuttleTrip?.legs?.[0]?.timedLeg?.service?.vehicleNumbers?.[0] || "",
                            transfers: outboundDestinationShuttleTrip.transfers,
                            fromName: isShuttleTrainStop ? outboundDestinationShuttleTrip?.legs?.[0]?.timedLeg?.start?.stopPlaceName
                                : outboundDestinationShuttleTrip?.legs?.[0]?.timedLeg?.end?.stopPlaceName || "",
                            toName: isShuttleTrainStop ? outboundDestinationShuttleTrip?.legs?.[0]?.timedLeg?.end?.stopPlaceName
                                : outboundDestinationShuttleTrip?.legs?.[0]?.timedLeg?.start?.stopPlaceName || "",
                            trainType: "shuttle"
                        } : undefined,
                        originInboundShuttle: filteredTrips.length === 0 ? undefined : inboundOriginShuttleTrip ? {
                            ...inboundOriginShuttleTrip,
                            id: bookingResponse.booking.id,
                            departureTime: this.convertTimeToArabic(inboundOriginShuttleTrip.startTime),
                            arrivalTime: this.convertTimeToArabic(inboundOriginShuttleTrip.endTime),
                            type: bookingResponse.booking.passengers[0]?.type || "Unknown",
                            duration: convertISO8601ToDuration(inboundOriginShuttleTrip.duration),
                            trainNumbers: inboundOriginShuttleTrip?.legs?.[0]?.timedLeg?.service?.vehicleNumbers?.[0] || "",
                            stops: inboundOriginShuttleTrip.transfers > 0 ? Array(inboundOriginShuttleTrip.transfers).fill("Stop") : [],
                            priceInfo: originInboundShuttlebookedOffer?.admissions?.[0]?.price
                                ? calculatePrice(originInboundShuttlebookedOffer.admissions[0].price) : '',
                            isSoldOut: false,
                            selectedPriceClass: this.getTrainClassTypeBaseOnProduct(bookedOffer?.products || []),
                            trainNo: inboundOriginShuttleTrip?.legs?.[0]?.timedLeg?.service?.vehicleNumbers?.[0] || "",
                            transfers: inboundOriginShuttleTrip.transfers,
                            fromName: inboundOriginShuttleTrip?.legs?.[0]?.timedLeg?.end?.stopPlaceName || "",
                            toName: inboundOriginShuttleTrip?.legs?.[0]?.timedLeg?.start?.stopPlaceName || "",
                            trainType: "shuttle"
                        } : undefined,
                        destinationInboundShuttle: filteredTrips.length === 0 ? undefined : inboundDestinationShuttleTrip ? {
                            ...inboundDestinationShuttleTrip,
                            id: bookingResponse.booking.id,
                            departureTime: this.convertTimeToArabic(inboundDestinationShuttleTrip.startTime),
                            arrivalTime: this.convertTimeToArabic(inboundDestinationShuttleTrip.endTime),
                            type: bookingResponse.booking.passengers[0]?.type || "Unknown",
                            duration: convertISO8601ToDuration(inboundDestinationShuttleTrip.duration),
                            trainNumbers: inboundDestinationShuttleTrip?.legs?.[0]?.timedLeg?.service?.vehicleNumbers?.[0] || "",
                            stops: inboundDestinationShuttleTrip.transfers > 0 ? Array(inboundDestinationShuttleTrip.transfers).fill("Stop") : [],
                            priceInfo: destinationInboundShuttlebookedOffer?.admissions?.[0]?.price
                                ? calculatePrice(destinationInboundShuttlebookedOffer.admissions[0].price) : '',
                            isSoldOut: false,
                            selectedPriceClass: this.getTrainClassTypeBaseOnProduct(bookedOffer?.products || []),
                            trainNo: inboundDestinationShuttleTrip?.legs?.[0]?.timedLeg?.service?.vehicleNumbers?.[0] || "",
                            transfers: inboundDestinationShuttleTrip.transfers,
                            fromName: inboundDestinationShuttleTrip?.legs?.[0]?.timedLeg?.start?.stopPlaceName || "",
                            toName: inboundDestinationShuttleTrip?.legs?.[0]?.timedLeg?.end?.stopPlaceName || "",
                            trainType: "shuttle"
                        } : undefined,
                    };

                    const searchStateObject: SearchStateType = {
                        fromName: stopManager.getStopNameById(trip.origin.stopPlaceRef),
                        toName: stopManager.getStopNameById(trip.destination.stopPlaceRef),
                        from: trip.origin.stopPlaceRef,
                        to: trip.destination.stopPlaceRef,
                        calc: "",
                        passengers: passengersObject
                    };

                    if (trip.direction === TripTypeEnum.OUT_BOUND) {
                        // OutBound Train
                        updateRecoilState("selectedTrainStateAtom", bookedTrips);

                        updateRecoilState("searchStateAtom", null);
                        // OutBound search state
                        updateRecoilState("searchStateAtom", searchStateObject);
                        // OutBound train date
                        updateRecoilState("selectedDateAtom", trip.startTime);
                    }
                    else if (trip.direction === TripTypeEnum.IN_BOUND) {
                        // InBound Train
                        updateRecoilState("selectedInboundTrainStateAtom", bookedTrips);
                        // InBound search sate
                        updateRecoilState("inboundSearchStateAtom", searchStateObject);
                        // InBound train date
                        updateRecoilState("inboundSelectedDateAtom", trip.startTime);
                        // InBound train end date
                        //updateRecoilState("inboundSelectedEndDateAtom", trip.endTime);
                    }
                }
                actionResult.data = bookingResponse
                actionResult.allFulFillOutBoundPassIds = allFulFillOutBoundPassIds
                actionResult.outBoundPassengerPriceList = outBoundPassengerPriceList
                actionResult.allBookedOfferWithoutExchange = allBookedOfferWithoutExchange
                actionResult.allBookedTripsWithoutExchange = allTripWithoutExchange
                actionResult.status = "success"
                actionResult.message = "Booking fetched successfully"
                // Return the required values
                return actionResult;
            }
            // Handle cases where response is not ok or answer is missing
            console.warn("GetBooking API call did not return 'ok' status or answer.");
            actionResult.message = "GetBooking API call did not return 'ok' status or answer."
            actionResult.status = "fail"
            return actionResult;
        } catch (error: any) {
            console.error("Error fetching booking detail:", error); // Changed error message slightly for clarity
            actionResult.message = "Error fetching booking detail"
            actionResult.status = "fail"
            return actionResult;
        }
        finally {
            return actionResult;
        }
    }

    private normalizePassengerPrices(
        bookingPassenger: IBookingPassenger,
        trips: IBookingTypeTrip[],
        bookedOffers: IBookedOffer[],
        isShuttle: boolean
    ) {
        const outboundTrips = trips.filter(t => t.direction === TripTypeEnum.OUT_BOUND)
        const inboundTrips = trips.filter(t => t.direction === TripTypeEnum.IN_BOUND)
        
        const outboundTripIds = new Set(outboundTrips.map(t => t.id))
        const inboundTripIds = new Set(inboundTrips.map(t => t.id))
        
        const outboundOffers = bookedOffers.filter(o => outboundTripIds.has(o.tripCoverage.coveredTripId))
        const inboundOffers = bookedOffers.filter(o => inboundTripIds.has(o.tripCoverage.coveredTripId))

        const sumPriceForPassenger = (passengerId: string, offers: IBookedOffer[]): number => {
            let total = 0
            offers.forEach(o => {
                const offerIsShuttle = isShuttleOffer(o)
                if (isShuttle === offerIsShuttle) {
                    o.admissions.forEach(a => {
                        if (a.passengerIds?.includes(passengerId)) {
                            total += calculatePrice(a.price)
                        }
                    })
                }
            })
            return total
        }

        if (bookingPassenger.outbound) {
            bookingPassenger.outbound.forEach(p => {
                p.passengerPrice = sumPriceForPassenger(p.id, outboundOffers)
            })
        }
        
        if (bookingPassenger.inbound) {
            bookingPassenger.inbound.forEach(p => {
                p.passengerPrice = sumPriceForPassenger(p.id, inboundOffers)
            })
        }
    }

    getShuttleTripIdsForShuttleTrain(bookedOffers: any) {
        return bookedOffers
            .filter((offer: any) => isShuttleOffer(offer))
            .map((offer: any) => offer.tripCoverage.coveredLegIds[0]);
    }

    getTrainClassTypeBaseOnProduct = (products: Product3[]) => {
        if (products.find(p => p.code === "SFC")) return `standardValue`
        else if (products.find(p => p.code === "SFF")) return "standardPro"
        else if (products.find(p => p.code === "PFF")) return "premium"
        else return null
    }

    public convertTimeToArabic(isoTime: string = ""): string {
        if (isoTime === "") return ""
        const date = new Date(isoTime);
        return date.toLocaleTimeString("en-AE", {
            timeZone: "Asia/Dubai",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    };

    getAllOfferWithoutExchange(bookedOffers: IBookedOffer[]) {
        const allBookedOfferWithoutExchange: IBookedOffer[] = []
        bookedOffers?.forEach((offer: IBookedOffer) => {
            const offerAdmissions = offer.admissions;
            const fulfilledAdmissions = offerAdmissions.filter(d => d.status !== "EXCHANGED" && d.status !== "EXCHANGE_ONGOING")
            if (fulfilledAdmissions.length > 0) {
                // Create a new offer with only the fulfilled admissions
                const newOffer = {
                    ...offer,
                    admissions: fulfilledAdmissions
                }
                allBookedOfferWithoutExchange.push(newOffer)
            }
        })
        return allBookedOfferWithoutExchange
    }

    getAllTripWithoutExchange(bookedOffers: IBookedOffer[], trips: BookingTypeTrip[]) {
        const allTripCoverageIds = bookedOffers.map(offer => offer.tripCoverage.coveredTripId)
        const allTripWithoutExchange: BookingTypeTrip[] = trips.filter(trip => allTripCoverageIds.includes(trip.id))
        return allTripWithoutExchange
    }

    getShuttleTripIds(bookedOffers: any) {
        return bookedOffers
            .filter((offer: any) => isShuttleOffer(offer))
            .map((offer: any) => offer.tripCoverage.coveredTripId);
    }

    getPassengerListByOfferAdmissions = async (
        passengers: ISelectedPassenger[],
        allTrip: IBookingTypeTrip[],
        bookedOffers: IBookedOffer[]
    ) => {
        const passengerList: IBookingPassenger = {
            outbound: [],
            inbound: []
        }
        const processedPrivacySeatingAdmissions = new Set<string>()
        
        // Get all outbound trips
        const outBoundTrips = allTrip.filter(i => i.direction === TripTypeEnum.OUT_BOUND)
        // Get all inbound trips
        const inBoundTrips = allTrip.filter(i => i.direction === TripTypeEnum.IN_BOUND)

        const outBoundTripIds = outBoundTrips.map(trip => trip.id)
        const outBoundOffers = bookedOffers.filter(offer => outBoundTripIds.includes(offer.tripCoverage.coveredTripId))

        if (outBoundOffers && outBoundOffers.length > 0) {
            const allTheOutBoundPassengerId = Array.from(new Set(
                outBoundOffers.flatMap(off => (off?.admissions || []).flatMap(add => add.passengerIds || []))
            ))
            
            passengerList.outbound = JSON.parse(JSON.stringify([...passengers]))
                .filter((d: ISelectedPassenger) => allTheOutBoundPassengerId.includes(d.id))
                .map((pass: ISelectedPassenger) => {
                    if (allTheOutBoundPassengerId.includes(pass.id)) {
                        const admissionsForPassenger = outBoundOffers.flatMap(off =>
                            (off?.admissions || []).filter(add => add.passengerIds?.includes(pass.id))
                        )
                        if (admissionsForPassenger.length > 0) {
                            let totalPrice = 0
                            admissionsForPassenger.forEach((passengerAdmission: any) => {
                                if (passengerAdmission.offerMode === "COLLECTIVE" && passengerAdmission.passengerIds.length > 1) {
                                    if (processedPrivacySeatingAdmissions.has(passengerAdmission.id)) {
                                        totalPrice += 0
                                    } else {
                                        totalPrice += calculatePrice(passengerAdmission.price)
                                        processedPrivacySeatingAdmissions.add(passengerAdmission.id)
                                    }
                                } else {
                                    totalPrice += calculatePrice(passengerAdmission.price)
                                }
                            })
                            pass.passengerPrice = totalPrice
                            // Prioritize FULFILLED status over EXCHANGED when passenger has multiple admissions
                            const fulfilledAdmission = admissionsForPassenger.find(a => a.status === "FULFILLED");
                            pass.passengerStatus = fulfilledAdmission ? fulfilledAdmission.status : admissionsForPassenger[0].status
                        }
                    }
                    return pass
                })
        }

        const inBoundTripIds = inBoundTrips.map(trip => trip.id)
        const inBoundOffers = bookedOffers.filter(offer => inBoundTripIds.includes(offer.tripCoverage.coveredTripId))
        
        if (inBoundOffers && inBoundOffers.length > 0) {
            const allTheInBoundPassengerId = Array.from(new Set(
                inBoundOffers.flatMap(off => (off?.admissions || []).flatMap(add => add.passengerIds || []))
            ))
            
            passengerList.inbound = JSON.parse(JSON.stringify([...passengers]))
                .filter((d: ISelectedPassenger) => allTheInBoundPassengerId.includes(d.id))
                .map((pass: ISelectedPassenger) => {
                    if (allTheInBoundPassengerId.includes(pass.id)) {
                        const admissionsForPassenger = inBoundOffers.flatMap(off => 
                            (off?.admissions || []).filter(add => add.passengerIds?.includes(pass.id))
                        )
                        if (admissionsForPassenger.length > 0) {
                            let totalPrice = 0
                            admissionsForPassenger.forEach((passengerAdmission: any) => {
                                if (passengerAdmission.offerMode === "COLLECTIVE" && passengerAdmission.passengerIds.length > 1) {
                                    if (processedPrivacySeatingAdmissions.has(passengerAdmission.id)) {
                                        totalPrice += 0
                                    } else {
                                        totalPrice += calculatePrice(passengerAdmission.price)
                                        processedPrivacySeatingAdmissions.add(passengerAdmission.id)
                                    }
                                } else {
                                    totalPrice += calculatePrice(passengerAdmission.price)
                                }
                            })
                            pass.passengerPrice = totalPrice
                            // Prioritize FULFILLED status over EXCHANGED when passenger has multiple admissions
                            const fulfilledAdmission = admissionsForPassenger.find(a => a.status === "FULFILLED");
                            pass.passengerStatus = fulfilledAdmission ? fulfilledAdmission.status : admissionsForPassenger[0].status
                        }
                    }
                    return pass
                })
        }
        
        return passengerList
    }
    calculatePassengerListByPrice = async (bookingResponse: IBookingResponse) => {
        //Calculate passenger price        
        //let allBookedOfferWithoutExchange: IBookedOffer[] = this.getAllOfferWithoutExchange(booking.bookedOffers)
        //const allTripWithoutExchange: IBookingTypeTrip[] = this.getAllTripWithoutExchange(allBookedOfferWithoutExchange, booking.trips)
        let allBookedOfferWithoutExchange: IBookedOffer[] = bookingResponse.booking.bookedOffers;
        const allTripWithoutExchange: BookingTypeTrip[] = bookingResponse.booking.trips;

        const trips = allTripWithoutExchange.filter(trip => {
            const shuttleTripIds = this.getShuttleTripIds(allBookedOfferWithoutExchange);
            return !(shuttleTripIds.includes(trip.id) && (trip.legs.length === trip.legs.filter(leg => leg.timedLeg.service.mode.ptMode === "BUS").length))
        });
        const shuttleTrips = allTripWithoutExchange.filter(trip => {
            const shuttleTripIds = this.getShuttleTripIds(allBookedOfferWithoutExchange);
            return shuttleTripIds.includes(trip.id);
        });
        const shuttleTripsOutbound = shuttleTrips.filter(t => t.direction === TripTypeEnum.OUT_BOUND)
        const shuttleTripsInbound = shuttleTrips.filter(t => t.direction === TripTypeEnum.IN_BOUND)

        // const filteredOffers = allBookedOfferWithoutExchange.filter(
        //     offer => offer.products.length > 0 && offer.products[0].code !== SHUTTLE_PRODUCT_CODE
        // );
        // allBookedOfferWithoutExchange = filteredOffers.length === 0 ? allBookedOfferWithoutExchange : filteredOffers;

        const outBoundPassengerPriceList: PassengerPriceDetails[] = []
        const inBoundPassengerPriceList: PassengerPriceDetails[] = []

        const updatedPassenger: ISelectedPassenger[] = bookingResponse.booking.passengers.map((pass: any) => {
            const selectedPass: ISelectedPassenger = {
                email: pass?.detail?.email || "",
                firstName: pass?.detail?.firstName || "",
                id: pass.id,
                lastName: pass?.detail?.lastName || "",
                mobile: "",
                remember: pass.remember || false,
                age: pass.age,
                seatingDetails: null,
                cards: pass.cards || [],
                prmNeeds: pass.prmNeeds || "",
            }
            return selectedPass
        })

        // Filter offers by type to avoid double-counting when trips have mixed offers
        const trainOffers = allBookedOfferWithoutExchange.filter(offer =>
            !isShuttleOffer(offer)
        )
        const shuttleOffers = allBookedOfferWithoutExchange.filter(offer =>
            isShuttleOffer(offer)
        )

        const trainPassenger = trips.length > 0 ? await this.getPassengerListByOfferAdmissions(updatedPassenger, trips, trainOffers) : { outbound: [], inbound: [] }
        const shuttlePassenger = shuttleTrips.length > 0 ? await this.getPassengerListByOfferAdmissions(updatedPassenger, shuttleTrips, shuttleOffers) : { outbound: [], inbound: [] }

        const allPassenger = await this.getPassengerListByOfferAdmissions(updatedPassenger, allTripWithoutExchange, allBookedOfferWithoutExchange)

        const allOutboundPassengers = JSON.parse(JSON.stringify(allPassenger.outbound || []))
        const allInboundPassengers = JSON.parse(JSON.stringify(allPassenger.inbound || []))

        allOutboundPassengers.forEach((pass: ISelectedPassenger) => {
            const priceDetails: PassengerPriceDetails = {
                pId: pass.id,
                pValue: pass.passengerPrice,
                pType: getUpperPassengerLabelByAge(pass.age || 0, pass),
                pStatus: pass.passengerStatus
            }
            outBoundPassengerPriceList.push(priceDetails)
        })
        allInboundPassengers.forEach((pass: ISelectedPassenger) => {
            const priceDetails: PassengerPriceDetails = {
                pId: pass.id,
                pValue: pass.passengerPrice,
                pType: getUpperPassengerLabelByAge(pass.age || 0, pass),
                pStatus: pass.passengerStatus
            }
            inBoundPassengerPriceList.push(priceDetails)
        })
        updateRecoilState("allPassengerPriceAndAgeGroupAtom", outBoundPassengerPriceList)
        updateRecoilState("allPassengerPriceAndAgeGroupInBoundAtom", inBoundPassengerPriceList)

        //PREBOOKED means this function called from processBooking function
        const allTheFulFillmentPassengers = outBoundPassengerPriceList.filter((d: any) => d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED")
        const allTheRefundedPassengers = outBoundPassengerPriceList.filter((d: any) => d.pStatus === "REFUNDED" || d.pStatus === "RELEASED")

        const outboundTrainEntries = this.buildPassengerGroupEntries(
            (trainPassenger.outbound || []).map((p: ISelectedPassenger) => ({
                pId: p.id,
                pValue: p.passengerPrice || 0,
                pType: getUpperPassengerLabelByAge(p.age || 0, p),
                pStatus: p.passengerStatus
            }))
        )
        const outboundShuttleEntry = this.buildShuttleEntry(
            shuttlePassenger.outbound || [],
            shuttleTripsOutbound,
            allBookedOfferWithoutExchange
        )

        updateRecoilState("isShuttleTripAtom", outboundShuttleEntry ? true : false)
        updateRecoilState("paymentOutBoundItemsDetailsAtom", outboundShuttleEntry
            ? [...outboundTrainEntries, outboundShuttleEntry]
            : outboundTrainEntries)
        //this.updatePaymentItems(allTheRefundedPassengers, "paymentOutBoundRefundItemsDetails")


        //PREBOOKED means this function called from processBooking function
        const allTheFulFillmentPassengersInBound = inBoundPassengerPriceList.filter((d: any) => d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED")
        const allTheRefundedPassengersInBound = inBoundPassengerPriceList.filter((d: any) => d.pStatus === "REFUNDED" || d.pStatus === "RELEASED")

        const inboundTrainEntries = this.buildPassengerGroupEntries(
            (trainPassenger.inbound || []).map((p: ISelectedPassenger) => ({
                pId: p.id,
                pValue: p.passengerPrice || 0,
                pType: getUpperPassengerLabelByAge(p.age || 0, p),
                pStatus: p.passengerStatus
            }))
        )
        const inboundShuttleEntry = this.buildShuttleEntry(
            shuttlePassenger.inbound || [],
            shuttleTripsInbound,
            allBookedOfferWithoutExchange
        )

        updateRecoilState("paymentInboundItemsDetailsAtom", inboundShuttleEntry
            ? [...inboundTrainEntries, inboundShuttleEntry]
            : inboundTrainEntries)
        //this.updatePaymentItems(allTheRefundedPassengersInBound, "paymentOutBoundRefundItemsDetailsInBoundAtom")
        return { outBoundPassengerPriceList, inBoundPassengerPriceList }
    }


    private buildPassengerGroupEntries = (priceList: PassengerPriceDetails[]) => {
        const counts: Record<string, number> = {}
        const sums: Record<string, number> = {}
        priceList.forEach((p) => {
            const key = p.pType || ""
            if (!key) return
            counts[key] = (counts[key] || 0) + 1
            sums[key] = (sums[key] || 0) + (p.pValue || 0)
        })
        const entries: any[] = []
        Object.keys(counts).forEach((type) => {
            entries.push({
                description: `${counts[type]} ${type}`,
                amount: sums[type]
            })
        })
        return entries
    }

    private buildShuttleEntry = (
        shuttlePassengers: ISelectedPassenger[],
        shuttleTrips: BookingTypeTrip[],
        bookedOffers: IBookedOffer[]
    ) => {
        const passengerIdSet = new Set((shuttlePassengers || []).map(p => p.id))
        if (passengerIdSet.size === 0) return null
        const tripIdSet = new Set((shuttleTrips || []).map(t => t.id))
        const relevantOffers = (bookedOffers || []).filter(o =>
            isShuttleOffer(o) && tripIdSet.has(o.tripCoverage.coveredTripId)
        )
        let count = 0
        let amount = 0
        relevantOffers.forEach(o => {
            o.admissions.forEach(a => {
                if (a.status === "FULFILLED" && a.passengerIds?.some(id => passengerIdSet.has(id))) {
                    count += 1
                    amount += calculatePrice(a.price)
                }
            })
        })
        if (count === 0) return null
        return { description: `${count} Shuttle`, amount }
    }

}

export const getBookingAction = new GetBookingAction()


